import {
    Typography,
    Stack,
    InputAdornment,
    Divider,
    Chip,
    CardHeader
} from '@mui/material'

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { FormProvider, RHFTextField } from './hook-form';
import IRPCalculationDetail from './IRPCalculationDetail';
import { useEffect, useState } from 'react';

//
// minimum wage
//
const PY_MIN_WAGE             = 2680373;
const PYG_SYMBOL              = 'PY₲';
const _millions               = hundreds => hundreds * 1000000;
const YEARLY_INCOME_THRESHOLD = _millions(80);

export default function IRPCalculatorForm() {
    // const [ netProfit, setNetProfit ] = useState(null);
    const [ taxes, setTaxes ]         = useState(null);
    const [ result, setResult ]       = useState(null);

    const addDots                     = (num) => {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    };
    const removeNonNumeric            = num => num.toString().replace(/[^0-9]/g, "");

    const defaultValues = {
      monthlyIncome:    addDots(PY_MIN_WAGE), 
      yearlyIncome:     addDots(PY_MIN_WAGE * 12),
      monthlyExpenses:  '',
      yearlyExpenses:   ''
    };
  
    const amountValidator = Yup.number()
                                .min(0, "El monto no puede ser negativo")
                                .nullable(false)
                                .typeError("Este valor debe ser un número")
                                .required("Este valor es requerido");

    const validationSchema = Yup.object().shape({
      monthlyIncome:    amountValidator,
      yearlyIncome:     amountValidator,
      monthlyExpenses:  amountValidator,
      yearlyExpenses:   amountValidator
    });

    const methods = useForm({
      resolver: yupResolver(validationSchema),
      defaultValues,
    });
  
    const {
      handleSubmit,
      setValue,
      getValues,
      // formState: { errors, isSubmitting },
    } = methods;
    
    // const onSubmit = e => {
    //   console.log(e);
    // }

    useEffect( e => {
      setTaxes(calculateIncomeTax());
    }, []);

    const calculateByTaxBracket = (taxableIncome) => {
      const byBracket           = [];
      let carryOver             = taxableIncome;
      let taxableBracketAmount  = taxableIncome;
      let bracketTaxes          = 0;  

      //
      // lower tax bracket: 8% on 0 - 50M when net income is above YEARLY_INCOME_THRESHOLD
      //
      if ( taxableIncome >= 0 ) {
        if ( taxableIncome > _millions(50) )
          taxableBracketAmount  = _millions(50)
        else
          taxableBracketAmount  = carryOver;
        
        bracketTaxes          = taxableBracketAmount;
        carryOver            -= bracketTaxes;

        byBracket.push({ amount: Math.floor(taxableBracketAmount * 0.08), percent_tax: 8, carry_over: carryOver})
      }
      
      //
      // mid tax bracket: 9% on 50M - 150M 
      //
      if ( taxableIncome >= (_millions(50) + 1) ) {
        if ( carryOver > _millions(150 - 50) )
          taxableBracketAmount  = _millions(150 - 50)
        else
          taxableBracketAmount  = carryOver
        
        bracketTaxes          = Math.floor(taxableBracketAmount * 0.09);
        carryOver             -= taxableBracketAmount;

        byBracket.push({ amount: bracketTaxes, percent_tax: 9, carry_over: carryOver })
      } 

      //
      // max tax bracket: 10% on 150M+
      //
      if ( taxableIncome >= _millions(150) + 1 ) {
        byBracket.push({ amount: Math.floor(carryOver * 0.1), percent_tax: 10, carry_over: carryOver })
      }

      return byBracket;
    };

    const calculateIncomeTax = () => {
      // console.log(getValues("yearlyExpense"));

      const yearlyIncome    = removeNonNumeric(getValues("yearlyIncome"));
      const yearlyExpenses  = removeNonNumeric(getValues("yearlyExpenses"));
      
      const netProfitAmount = yearlyIncome - yearlyExpenses;
      const formatter       = Intl.NumberFormat('es-PY');

      if ( (yearlyIncome === 0 && yearlyExpenses === 0) || yearlyIncome === undefined || yearlyExpenses === undefined ) {
        setResult("Introduzca valores para realizar el cálculo");
        return null;
      }
      
      if ( yearlyIncome < YEARLY_INCOME_THRESHOLD ) {
        setResult(<>
          Como sus ingresos gravables anuales son menores al límite inferior de <Chip label={PYG_SYMBOL + ' ' + formatter.format(YEARLY_INCOME_THRESHOLD)}/>,
          &nbsp;<i><b>no tiene obligaciones fiscales en el IRP</b></i> 🎉.
          </>);
        
        return null;
      }
      
      let taxes                   = 0;
      const taxesByBracket        = calculateByTaxBracket(netProfitAmount);
      const sign                  = (netProfitAmount === 0 ? "neutra" : (netProfitAmount > 0 ? "positiva" : "negativa"));
      const bracketsCount         = taxesByBracket.length > 1 ? `${taxesByBracket.length} tramos` : `1 tramo`;
      const percentagesOnBracket  = taxesByBracket.map(bracket => {
        return (<><Chip label={formatter.format(bracket.percent_tax) + '%'}/> {', '} </>);
      });
      

      taxesByBracket.forEach(bracket => {
        if ( taxes === undefined)
          taxes = 0;
        
        // console.log(bracket)
        taxes += bracket.amount;
      });

      

      // console.log(">", taxes, taxesByBracket);

      // setNetProfit(formatter.format(netProfitAmount));
      // setTaxes(formatter.format(taxes));

      const taxesExplainer  = taxes === 0 && yearlyExpenses > 0 ? 
                                  <><i><b>no tiene impuestos a pagar 🎉</b></i></> : 
                                  (<>
                                    y equivale a un monto de <Chip color="primary" label={PYG_SYMBOL + ' ' + formatter.format(taxes)}/>
                                    &nbsp;en impuestos a pagar
                                  </>);

      

      if ( netProfitAmount <= 0 ) {
        setResult(<>
          Como sus ingresos anuales son mayores a <Chip label={PYG_SYMBOL + ' ' + formatter.format(YEARLY_INCOME_THRESHOLD)}/>
          &nbsp;pero con una renta neta <i>{sign}</i> e igual a <Chip color="success" label={PYG_SYMBOL + ' ' + formatter.format(netProfitAmount)}/>,
          &nbsp;{taxesExplainer}.
        </>);
        return;
      }

      setResult(
        <>
        Como sus ingresos anuales son mayores a <Chip label={PYG_SYMBOL + ' ' + formatter.format(YEARLY_INCOME_THRESHOLD)}/>
        &nbsp;con una renta neta <i>{sign}</i> e igual a <Chip color={ netProfitAmount < 0 ? "success" : "default" } label={PYG_SYMBOL + ' ' + formatter.format(netProfitAmount)}/>,
        &nbsp;su porcentaje de IRP es aplicado en {bracketsCount} de {percentagesOnBracket}
        {taxesExplainer}.
        </>
      );

      return taxesByBracket;
    }

    const setNumericValue = (fieldName, amount) => {
      setValue(fieldName, addDots(amount));
    }

    const onMonthlyIncomeChange = function (e) {

      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("monthlyIncome", addDots(amount));
      setNumericValue("yearlyIncome", addDots(Math.ceil(amount * 12)));

      setTaxes(calculateIncomeTax());
    }

    const onYearlyIncomeChange = e => {
      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("yearlyIncome", addDots(amount));
      setNumericValue("monthlyIncome", addDots(Math.ceil(amount / 12)));

      setTaxes(calculateIncomeTax());
    }

    const onMonthlyExpenseChange = function (e) {

      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("monthlyExpenses", addDots(amount));
      setNumericValue("yearlyExpenses", addDots(Math.ceil(amount * 12)));

      setTaxes(calculateIncomeTax());
    }

    const onYearlyExpenseChange = e => {
      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("yearlyExpenses", addDots(amount));
      setNumericValue("monthlyExpenses", addDots(Math.ceil(amount / 12)));

      setTaxes(calculateIncomeTax());
    }

    return (
      <FormProvider methods={methods}>
        
        <Stack spacing={3}>

          {/* <Typography variant="h5" > Ingresos y egresos gravables </Typography> */}
          <CardHeader title="Ingresos y egresos gravables"/>

          <Divider sx={{ display: { xs: 'block', sm: 'none', md: 'none' } }}> ➕ <strong>Ingresos</strong> </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

            <RHFTextField name="monthlyIncome" label="Ingresos Mensuales" 
              onChange={onMonthlyIncomeChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
            
            <Typography> = </Typography>
            
            <RHFTextField name="yearlyIncome" label="Ingresos Anuales"
              onChange={onYearlyIncomeChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>

          </Stack>
          
          <Divider sx={{ display: { xs: 'block', sm: 'none', md: 'none' } }}> ➖ <strong>Egresos</strong> </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            
            <RHFTextField name="monthlyExpenses" label="Egresos Mensuales" 
              onChange={onMonthlyExpenseChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
            
            <Typography> = </Typography>

            <RHFTextField name="yearlyExpenses" label="Egresos Anuales" 
              onChange={onYearlyExpenseChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
          </Stack>

          <Divider />
                
          <Stack direction={{ xs: 'column', sm: 'column' }} spacing={0} alignItems="center">
            <Typography variant="body1">
              {result}
              <br />
              
            </Typography>

            {taxes && <IRPCalculationDetail calculatedTaxes={taxes}/>}
          </Stack>
        </Stack>

        </FormProvider>

        
    );
}
